import { useState, useRef, useContext, useEffect } from "react";
import { useSelector } from "react-redux";

// context
import { DispatchContext } from "bodycodi/cmap/app/components/common/context";
import { DispatchContext as HomeDispatchContext } from "bodycodi/cmap/app/components/cmap2010/home";

// components
import UpdateCommunityHeader from "./Header";
import {
  AddCommunityMembers,
  RenderVideos,
  RenderVideoLinks,
  RenderImages,
  BottomButtons,
  CommunityTitleInputField,
} from "../common";

// redux
import { RootState } from "bodycodi/cmap/app/redux/store";

// types
import { UpdateCommunityProps } from "./types";

// utils
import { isEmpty } from "bodycodi/cmap/app/js/common";
import { api } from "bodycodi/cmap/app/js/api";

const UpdateCommunity = ({ details, handleClose }: UpdateCommunityProps) => {
  const { seqPartner, seqPartnerBranch, seqPartnerCoach } = useSelector(
    (state: RootState) => state.partner
  );
  const { dispatch } = useContext(DispatchContext);
  const { dispatch: homeDispatch } = useContext(HomeDispatchContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddLink, setOpenAddLink] = useState<boolean>(false);
  const imageFileRef = useRef<any>(null);
  const [textAreaContent, setTextAreaContent] = useState<string>("");
  const [chosenMembers, setChosenMembers] = useState<any>({});
  const [title, setTitle] = useState<string>("");
  const [videoLinks, setVideoLinks] = useState<any>([]);
  const [videoFiles, setVideoFiles] = useState<any>([]);
  const [imageFiles, setImageFiles] = useState<any>([]);
  const [imageToEdit, setImageToEdit] = useState<any>({});
  const [linkToEdit, setLinkToEdit] = useState<string>("");

  const fetchCommunity = async () => {
    dispatch({ type: "LOADING_START" });
    try {
      await api.get(`/community/${seqPartnerCoach}/posts/${details?.uploadId}`).then(({ data }) => {
        setTitle(data.uploadName);

        // selected members
        if (!isEmpty(data.members)) {
          for (let i = 0; i < data.members.length; i++) {
            chosenMembers[data.members[i].seqMember] = data.members[i];
          }
          setChosenMembers({ ...chosenMembers });
        }

        // media files
        if (!isEmpty(data.files)) {
          for (let i = 0; i < data.files.length; i++) {
            const file = data.files[i];
            if (file.fileKindCode === "A") {
              imageFiles.push({
                index: imageFiles.length,
                data: file,
                fileNm: file.fileName,
              });
            } else if (file.fileKindCode === "B") {
              videoFiles.push({
                fileId: file.fileId,
                fileUrl: file.fileUrl,
                thumbNail: file.thumbNail,
              });
            } else if (file.fileKindCode === "C") {
              videoLinks.push({ fileId: file.fileId, link: file.fileUrl });
            }
          }
          setImageFiles(imageFiles);
          setVideoFiles(videoFiles);
          setVideoLinks(videoLinks);
        }

        // textarea
        setTextAreaContent(details.uploadContent);
        dispatch({ type: "LOADING_END" });
      });
    } catch (error) {
      dispatch({ type: "LOADING_END" });
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCommunity();
  }, []);

  const updateCommunity = async () => {
    dispatch({ type: "LOADING_START" });
    setLoading(true);

    const requestBody: any = {};
    const videoData: any = [];
    const imageData: any = [];
    const linkData: any = [];
    const memberVoList: any = [];

    // video files
    if (!isEmpty(videoFiles)) {
      videoFiles.forEach((item: any) => {
        const newVideoData = {
          fileId: item.fileId,
          fileKindCode: "B",
        };
        videoData.push(newVideoData);
      });
    }

    // video links
    if (!isEmpty(videoLinks)) {
      videoLinks.forEach((item: any) => {
        const newVideoLink = {
          fileId: item.fileId,
          fileKindCode: "C",
        };
        linkData.push(newVideoLink);
      });
    }

    // images
    if (!isEmpty(imageFiles)) {
      imageFiles.forEach((item: any) => {
        const newImage = {
          fileId: item.data.fileId,
          fileKindCode: "A",
        };
        imageData.push(newImage);
      });
    }

    // members
    if (!isEmpty(chosenMembers)) {
      for (const item in chosenMembers) {
        const member = chosenMembers[item];
        memberVoList.push(member);
      }
    }

    // seqData
    requestBody.files = [...videoData, ...imageData, ...linkData];
    requestBody.title = title;
    requestBody.content = textAreaContent;
    requestBody.members = memberVoList;
    requestBody.seqPartner = seqPartner;
    requestBody.seqPartnerBranch = seqPartnerBranch;
    requestBody.seqPartnerCoach = seqPartnerCoach;

    // request
    await api.post(`/community/${details.uploadId}/update`, { ...requestBody }).then((res) => {
      homeDispatch({ type: "UPDATE_COMUNITY" });
      dispatch({ type: "LOADING_END" });
      setLoading(false);
      handleClose();
      dispatch({ type: "POP_UP_OPEN", id: "COMUNITY_DTL_POP", open: { comunityDtlPop: false } });
    });
  };

  return (
    <>
      <UpdateCommunityHeader
        handleClose={handleClose}
        clickUpd={updateCommunity}
        disabled={loading || isEmpty(chosenMembers) || textAreaContent.length < 1 || isEmpty(title)}
      />

      <div className="popup-body add-comunity-pop">
        <AddCommunityMembers chosenMembers={chosenMembers} setChosenMembers={setChosenMembers} />
        <CommunityTitleInputField value={title} onChange={setTitle} />

        <div
          className="add-comunity-decs"
          onClick={() => {
            setOpenAddLink(false);
            setLinkToEdit("");
          }}
        >
          {videoFiles.length > 0 && (
            <RenderVideos videoFiles={videoFiles} setVideoFiles={setVideoFiles} />
          )}
          {videoLinks.length > 0 && (
            <RenderVideoLinks
              videoLinks={videoLinks}
              setVideoLinks={setVideoLinks}
              setLinkToEdit={setLinkToEdit}
              setOpenAddLink={setOpenAddLink}
            />
          )}
          {imageFiles.length > 0 && (
            <RenderImages
              imageFiles={imageFiles}
              setImageFiles={setImageFiles}
              setImageToEdit={setImageToEdit}
            />
          )}
          <textarea
            placeholder="내용 입력 (필수)"
            value={textAreaContent}
            onChange={(e) => setTextAreaContent(e.target.value)}
            maxLength={3000}
          />
        </div>

        <BottomButtons
          imageFiles={imageFiles}
          videoFiles={videoFiles}
          imageToEdit={imageToEdit}
          setImageFiles={setImageFiles}
          setVideoFiles={setVideoFiles}
          setImageToEdit={setImageToEdit}
          imageFileRef={imageFileRef}
          openAddLink={openAddLink}
          setOpenAddLink={setOpenAddLink}
          videoLinks={videoLinks}
          setVideoLinks={setVideoLinks}
          linkToEdit={linkToEdit}
          setLinkToEdit={setLinkToEdit}
        />
      </div>
    </>
  );
};

export default UpdateCommunity;
